import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { useRef, useState, useEffect } from "react";
import { t } from "../locale";
import {
    captureSnapshot,
    useCamera,
    useCamera2,
    getRequestData,
    resizeImage
} from "../helpers/helpers";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useNavigate } from "react-router-dom";

import QRCodeModal from "../components/QRCodeModal";
import KDocAnalyzeModal from "../components/KDocAnalyzeModal";
import StepGuide from "../components/StepGuide";
import { checkDocumentData, getQRCode } from "../service/image";
import VerificationStepNav from "../components/VerificationStepNav";
import ExtractedDataModal from "../components/ExtractedDataModal";

function PassportVerification() {
    const navigate = useNavigate();

    const documentImageData = useRef<string>('')
    const documentImageDataDocumentAnalyze = useRef<string>('')
    const countDocCheck = useRef(0)

    const pathSegments = window.location.pathname.split("/");
    const companyId = pathSegments[1];
    const requestId = pathSegments[2];
    const passport = pathSegments[4]

    const [hasAccess, setHasAccess] = useState(true);
    const [expiredPage, setExpiredPage] = useState(false);

    const [facingMode, setFacingMode] = useState('environment');
    const [isMobile, setIsMobile] = useState(false);

    const [frontBorderColor, setFrontBorderColor] = useState('border-primary');
    const [secondsLeftFront, setSecondsLeftFront] = useState(10);
    const [timeLabelFront, setTimeLabelFront] = useState(true)
    const [labelWait, setLabelWait] = useState(false)
    const [label, setLabel] = useState(t('failed'))
    const [textColor, setTextColor] = useState('text-primary')

    const [buttonDisabled, setButtonDisabled] = useState('')
    const [spaceHeight, setSpaceHeight] = useState('mt-2')
    const [captured, setCaptured] = useState(false)
    const [blinkLabel, setBlinkLabel] = useState('')

    const [showModal, setShowModal] = useState(false);
    const [qrCodeSrc, setQrCodeSrc] = useState('')
    const [qrCodeMessage, setQrCodeMessage] = useState('')

    const [showAlert, setShowAlert] = useState(false);
    const [fields, setFields] = useState<string[] | undefined>([]);

    const [showExtractedModal, setShowExtractedModal] = useState(false);
    const [extractedData, setExtractedData] = useState<Record<string, string | null> | null>(null);
    const [fieldResults, setFieldResults] = useState<{ field: string, valid: boolean }[]>([]);

    const back = () => {
        navigate(`/${companyId}/${requestId}/p`)
    }

    const { mobileNav, desktopNav } = VerificationStepNav({
        currentStep: 2,
        companyId,
        requestId,
        onBack: back,
        spaceHeight
    })

    const fileInputDocument = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        fetchData();
        qrCode();
        handleDeviceDetection();
        if (isMobile) {
            setSpaceHeight('')
        }
    }, [])

    const init = () => {
        setFacingMode('environment')
        setLabelWait(false)
        setTimeLabelFront(true)
        setSecondsLeftFront(10);
    }

    useCamera2(facingMode, isMobile);
    useCamera(facingMode, isMobile);


    const fetchData = async () => {
        try {
            const data = await getRequestData(companyId, requestId);

            if (data) {
                if (data.status === "EXPIRED") {
                    setExpiredPage(false);
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setHasAccess(false);
        }
    }

    const handleDeviceDetection = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(userAgent);
        const isTablet = /(ipad|macintosh|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

        if (isMobile || isTablet) setIsMobile(true);
    }

    const changeCamera = () => {
        setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
    }

    function scrollToBottom() {
        setTimeout(() => {
            window.scrollTo(0, document.body.scrollHeight);
        }, 0);
    }

    const handleDocumentUpload = () => {
        if (fileInputDocument.current) {
            fileInputDocument.current.click(); // Trigger the file input click
        }
    }


    const isHeic = (file: File): boolean =>
        file.type === 'image/heic' ||
        file.type === 'image/heif' ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif');

    const convertHeicToFile = async (file: File): Promise<File> => {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/convert-image`, {
            method: 'POST',
            body: formData,
        });
        if (!res.ok) throw new Error('Konvertierung fehlgeschlagen');
        const { base64 } = await res.json() as { base64: string };
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new File([bytes], file.name.replace(/\.(heic|heif)$/i, '.png'), { type: 'image/png' });
    };

    const documentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        countDocCheck.current++;
        setLabelWait(true)
        setLabel(t('wait'))
        setButtonDisabled('disabled')
        let file = event.target.files?.[0]
        if (file) {
            if (isHeic(file)) {
                try { file = await convertHeicToFile(file); } catch (e) { console.warn('HEIC conversion failed, using original', e); }
            }

            const reader = new FileReader()
            reader.onloadend = () => {
                const base64String = reader.result as string
                documentImageDataDocumentAnalyze.current = base64String.replace(/^data:image\/(jpg|jpeg|png|heic|heif|webp);base64,/, '')
            };
            reader.readAsDataURL(file);

            setCaptured(true)
            resizeImage(file, 1920, 1440, async (resizedBase64) => {
                const base64String = resizedBase64.replace(/^data:image\/(jpg|jpeg|png|heic|heif|webp);base64,/, '')
                documentImageData.current = base64String
                saveBase64ToLocalStorage('documentImage', documentImageData.current)
                setTimeLabelFront(false)


                const docImageElement = document.getElementById("imageDocument") as HTMLImageElement
                if (docImageElement) {
                    docImageElement.src = "data:image/jpeg;base64," + resizedBase64

                    docImageElement.style.height = '200px'
                }

                setBlinkLabel('blinking-label')
                setLabel(t('waitDocument'))

                await compareImage()
            });
        }
        scrollToBottom()
    }

    const captureFrontId = async () => {

        countDocCheck.current++;
        setButtonDisabled('disabled')
        const video = document.getElementById("videoElement") as HTMLVideoElement;
        let capturedDocImageElement: HTMLImageElement | null = null;

        if (!isMobile) {
            const countdown = setInterval(() => {
                setSecondsLeftFront(prevSeconds => {
                    if (prevSeconds <= 1) {
                        clearInterval(countdown);
                        return 0;
                    }
                    return prevSeconds - 1;
                });
            }, 1000);
        }

        setCaptured(true)
        setTimeout(async () => {
            if (!isMobile) {

                const result = await captureSnapshot(
                    video,
                    "DOCUMENT",
                    documentImageData.current
                );

                setLabelWait(true)
                setLabel('waitDocument')
                setTimeLabelFront(false)
                scrollToBottom();

                documentImageData.current = result.documentImageData;
                saveBase64ToLocalStorage('documentImage', documentImageData.current)
                if (!capturedDocImageElement) {
                    capturedDocImageElement = document.getElementById("imageDocument") as HTMLImageElement;
                }
                if (capturedDocImageElement) {
                    capturedDocImageElement.src = "data:image/jpeg;base64," + documentImageData.current;
                }

            }

            compareImage()

        }, 10000);
    }

    const saveBase64ToLocalStorage = (key: string, base64: string) => {
        try {
            localStorage.setItem(key, base64);
            console.log('Base64 string saved successfully.');
        } catch (error) {
            console.error('Error saving Base64 string:', error);
        }
    };

    const compareImage = async () => {
        let data;
        if (isMobile) {
            data = await checkDocumentData(documentImageDataDocumentAnalyze.current, requestId, companyId, 'passport');
        } else {
            data = await checkDocumentData(documentImageData.current, requestId, companyId, 'passport');
        }

        if (data.validation === true) {
            changeCamera();
            setFrontBorderColor('border-success');
            setLabelWait(false)
            setTextColor('text-success')
            setLabel(t('success'))
            setTimeLabelFront(false)
            setQrCodeMessage('')
        } else {
            if (countDocCheck.current === 1) {
                setFrontBorderColor('border-danger')
                setTextColor('text-danger')
                setLabel(t('fail'))
                setSecondsLeftFront(0);
                setBlinkLabel('')
                setButtonDisabled('')
            } else {
                setLabel(t('tryAgain'))
                setFrontBorderColor('border-danger')
                setBlinkLabel('')
            }
            if (!isMobile) {
                setQrCodeMessage('changeDeviceText')
                setShowModal(true)
            }
        }

        if (data.extracted) {
            setExtractedData(data.extracted)
            setFieldResults(data.data ?? [])
            setShowExtractedModal(true)
        } else if (data.validation !== true) {
            setTimeout(() => {
                init();
                setButtonDisabled('')
            }, 3000);
        }
    }

    const handleClose = () => {
        setShowModal(false);
    };

    const handleCloseFieldPrompt = () => {
        setShowAlert(false)
    }

    const qrCode = async () => {
        const res = await getQRCode(companyId, requestId, passport)
        setQrCodeSrc(res.qrCode)
    }

    const bothModalsVisible = showModal && showExtractedModal;

    return (
        <>
            {!isMobile && (
                <QRCodeModal
                    qrCode={qrCodeSrc}
                    message={qrCodeMessage}
                    show={showModal}
                    onClose={handleClose}
                    position={bothModalsVisible ? 'left' : 'center'}
                ></QRCodeModal>
            )}
            <KDocAnalyzeModal show={showAlert} fields={fields} onClose={handleCloseFieldPrompt}></KDocAnalyzeModal>
            <ExtractedDataModal
                show={showExtractedModal}
                data={extractedData}
                fieldResults={fieldResults}
                position={bothModalsVisible ? 'right' : 'center'}
                backdrop={!bothModalsVisible}
                onConfirm={() => {
                    setShowExtractedModal(false)
                    if (frontBorderColor === 'border-success') {
                        navigate(`/${companyId}/${requestId}/face_identify`)
                        return
                    }
                    const invalidFields = fieldResults.filter(r => !r.valid).map(r => r.field)
                    if (invalidFields.length > 0) {
                        navigate(`/${companyId}/${requestId}/p`, { state: { invalidFields } })
                        return
                    }
                    setTimeout(() => {
                        init();
                        setButtonDisabled('')
                    }, 500)
                }}
            />

            {mobileNav}
            {hasAccess ? (
                <Container hidden={expiredPage} className={`overflow-hidden`}>
                    <Row className="row overflow-auto">
                        {desktopNav}

                        {passport &&
                            (
                                <Col className={`d-flex flex-column h-sm-100 px-sm-5 ${spaceHeight}`}>
                                    <main className={`row overflow-auto px-sm-5 position-relative  ${spaceHeight}`}>
                                        <Col className="pt-4 pt-sm-5 text-center text-sm-start mt-n1">
                                            <h4 className="mb-4">{t('passportTitle')}</h4>

                                            <Row className="mt-6 mt-sm-0">
                                                <div className="col-sm-7  mx-auto mx-sm-0">
                                                    <h5>{t('passportSubTitle')}</h5>
                                                    {!isMobile && <div className={`rounded-3 border ${frontBorderColor} border-3 video-frame w-auto`}>
                                                        {<video className="h-100 w-100 video-element" id="videoElement" autoPlay muted></video>}
                                                        {timeLabelFront && (secondsLeftFront !== 0 && <span className="text-primary h5 mx-0 text-sm-start">{secondsLeftFront}</span>)}
                                                        {labelWait && <div className={`mt-2`}> <span className={`text-primary mt-4 h5 ${textColor} blinking-label`}>{label}</span> </div>}
                                                    </div>
                                                    }
                                                </div>

                                                <div className='col-sm-3 text-center mt-4 align-content-center'>
                                                    <StepGuide
                                                        step={1}
                                                        text={t('guideStepPassport')}
                                                        show={!captured}
                                                        arrowAlign='center'
                                                    />
                                                    <Button
                                                        className={`btn-primary rounded-pill mt-2 d-none d-sm-inline ${buttonDisabled}`}
                                                        onClick={captureFrontId}
                                                        style={{ textTransform: 'none' }}
                                                    >
                                                        {t('buttonFrontCapture')}
                                                    </Button>
                                                    <p className="my-2 d-none d-sm-block fs-5">{t('or')}</p>
                                                    <input
                                                        type="file"
                                                        accept="image/*,.heic,.heif;capture=camera"
                                                        onChange={documentUpload}
                                                        ref={fileInputDocument}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <Button
                                                        className={`btn-primary rounded-pill mt-2 mt-sm-0 ${buttonDisabled}`}
                                                        onClick={handleDocumentUpload}
                                                        style={{ textTransform: 'none' }}
                                                    >
                                                        {t('passportUpload')}
                                                    </Button>
                                                    <div className="mt-4 mb-4">
                                                        {isMobile && labelWait && <span className={`text-primary h4 ${blinkLabel}`}>{label}</span>}
                                                    </div>
                                                </div>

                                            </Row>
                                            <Row className="">
                                                <div className="col-sm-7 mt-4 mx-auto mx-sm-0">
                                                    {captured &&
                                                        <div className={` mt-4 rounded-3 border border-3 video-frame w-auto ${frontBorderColor}`}>
                                                            <img
                                                                id="imageDocument"
                                                                className={`w-100 h-100 object-fit-cover`}
                                                                alt=""
                                                            />
                                                        </div>
                                                    }
                                                </div>
                                            </Row>
                                            <hr className="my-5 mb-3" />
                                            <p className="my-5">&nbsp;</p>
                                            <div className="position-absolute button-instructions d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary rounded-pill"
                                                    type="button"
                                                    onClick={() => { setQrCodeMessage(''); setShowModal(true) }}
                                                >
                                                    <i className="bi bi-qr-code me-1" />
                                                    {t('qrCode')}
                                                </button>
                                                <button className="btn btn-sm btn-outline-secondary rounded-pill" type="button"
                                                    data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight" aria-controls="offcanvasRight">{t('instructions')}
                                                </button>
                                            </div>
                                        </Col>

                                        <div className="offcanvas offcanvas-end" id="offcanvasRight"
                                            aria-labelledby="offcanvasRightLabel">
                                            <div className="offcanvas-header">
                                                <h5 id="offcanvasRightLabel">{t('instructions')}</h5>
                                                <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas"
                                                    aria-label="Close"></button>
                                            </div>
                                            <div className="offcanvas-body">
                                                <p className="mb-4">{t('passportInstructionTitle')}</p>
                                                <ul className="text-start list-unstyled mb-4">
                                                    <li>✔️ {t('firstInstruction')}</li>
                                                    <li>✔️ {t('secondInstruction')}</li>
                                                    <li>✔️ {t('thirdInstruction')}</li>
                                                </ul>
                                                <p className="mb-4">{t('example')}</p>
                                                <p className="mb-0">{t('passportInstructions4')}</p>
                                                <img src="/images/Passport.jpg" className="img-fluid mt-2" />
                                            </div>
                                        </div>
                                    </main>
                                </Col>
                            )
                        }
                    </Row>
                </Container>
            ) : (
                <Alert variant="danger" className="mt-3">
                    {t("accessDenied")}
                </Alert>
            )}


            <div className="py-2" hidden={!expiredPage}>
                <h4>{t("requestExpired")}</h4>
                <p>{t("accessExpired")}</p>
            </div>
        </>
    );
};

export default PassportVerification;